import { type ReactNode, isValidElement } from "react";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../../../useAppTheme";
import Box from "../../../Box";

export type PreTestTableColumn = {
	label: string;
	flex?: number;
};

type Props = {
	columns: PreTestTableColumn[];
	rows: ReactNode[][];
	caption?: string;
};

export const PreTestTable = ({ columns, rows, caption }: Props) => {
	const theme = useAppTheme();
	const border = theme.colors.outlineVariant;

	return (
		<Box
			borderRadius={theme.roundness * 2}
			borderWidth={1}
			borderColor={border}
			overflow="hidden"
		>
			{caption ? (
				<Box
					backgroundColor={theme.colors.primaryContainer}
					paddingHorizontal={16}
					paddingVertical={12}
					borderBottomWidth={1}
					borderBottomColor={border}
				>
					<Text
						variant="titleSmall"
						style={{ color: theme.colors.onPrimaryContainer }}
					>
						{caption}
					</Text>
				</Box>
			) : null}

			<Box
				flexDirection="row"
				backgroundColor={theme.colors.secondaryContainer}
			>
				{columns.map((column, index) => (
					<Box
						key={column.label}
						flex={column.flex ?? 1}
						paddingHorizontal={16}
						paddingVertical={12}
						borderRightWidth={index < columns.length - 1 ? 1 : 0}
						borderRightColor={border}
					>
						<Text
							variant="titleSmall"
							style={{ color: theme.colors.onSecondaryContainer }}
						>
							{column.label}
						</Text>
					</Box>
				))}
			</Box>

			{rows.map((cells, rowIndex) => (
				<Box
					// biome-ignore lint/suspicious/noArrayIndexKey: rows are static, presentational
					key={rowIndex}
					flexDirection="row"
					alignItems="stretch"
					borderTopWidth={1}
					borderTopColor={border}
				>
					{cells.map((cell, cellIndex) => (
						<Box
							// biome-ignore lint/suspicious/noArrayIndexKey: cells map to fixed columns
							key={cellIndex}
							flex={columns[cellIndex]?.flex ?? 1}
							paddingHorizontal={16}
							paddingVertical={12}
							borderRightWidth={cellIndex < cells.length - 1 ? 1 : 0}
							borderRightColor={border}
						>
							{typeof cell === "string" || typeof cell === "number" ? (
								<Text
									variant="bodyMedium"
									style={{ color: theme.colors.onSurfaceVariant }}
								>
									{cell}
								</Text>
							) : isValidElement(cell) ? (
								cell
							) : null}
						</Box>
					))}
				</Box>
			))}
		</Box>
	);
};
